import React from 'react';

import { observer } from 'mobx-react';
import { ApplicationUuid, Task, TaskStatus } from '@bringg/types';
import {
	OnTimeStatus,
	getTaskStatus,
	getTaskStatusClass,
	useRoutesMonitoringTranslations
} from '@bringg-frontend/monitoring';

import { useStores } from 'bringg-web/recipes';
import useTaskTooltipTranslation from './use-task-tooltip-translation';
import StatusDot from '../../status-dot/status-dot';

import styles from '../tooltip.module.scss';

interface TaskStatusLabelProps {
	task: Task;
}

const TaskStatusLabel = ({ task }: TaskStatusLabelProps) => {
	const { status } = useRoutesMonitoringTranslations();
	const {
		partialFulfilled,
		cancelled,
		lateCompleted,
		lateNotCompleted,
		earlyCompleted,
		earlyNotCompleted,
		onTimeCompleted,
		onTimeNotCompleted
	} = useTaskTooltipTranslation();
	const { merchantConfigurationsStore } = useStores();
	const isRealtimeEtaEnabled = !!merchantConfigurationsStore.findApplication(ApplicationUuid.RealtimeEtaApp);

	const isPartiallyFulfilled = task.status === TaskStatus.Done && task.aggregations?.has_any_rejected_inventory;

	const value = (() => {
		if (task.status === TaskStatus.Cancelled) {
			return cancelled;
		}
		const taskStatus = getTaskStatus(task);
		switch (taskStatus) {
			case OnTimeStatus.late:
				return task.status === TaskStatus.Done ? lateCompleted : lateNotCompleted;
			case OnTimeStatus.early:
				return task.status === TaskStatus.Done ? earlyCompleted : earlyNotCompleted;
			default:
				return task.status === TaskStatus.Done ? onTimeCompleted : onTimeNotCompleted;
		}
	})();

	return (
		<>
			<div className={styles.status}>
				<div data-test-id={`status-${value}`} className={styles.valueContainer}>
					<span className={styles.key}>{status}:</span> <span className={styles.value}>{value}</span>
				</div>
				<StatusDot className={styles.dot} statusClass={getTaskStatusClass(task, isRealtimeEtaEnabled)} />
			</div>
			{isPartiallyFulfilled && <div className={styles.key}>{partialFulfilled}</div>}
		</>
	);
};

export default observer(TaskStatusLabel);
